import { FolderKanban, Landmark, LifeBuoy, Mail, Settings, UserCog, Users } from 'lucide-react'

import { accessNavigation } from '@/modules/access/navigation'
import { auditNavigation } from '@/modules/audit/navigation'
import { companiesNavigation } from '@/modules/companies/navigation'
import { dashboardNavigation } from '@/modules/dashboard/navigation'
import { financeNavigation } from '@/modules/finance/navigation'
import { exportsNavigation } from '@/modules/exports/navigation'
import { newsNavigation } from '@/modules/news/navigation'
import { notificationsNavigation } from '@/modules/notifications/navigation'
import { segmentationNavigation } from '@/modules/segmentation/navigation'
import { settingsNavigation } from '@/modules/settings/navigation'
import { projectsNavigation } from '@/modules/projects/navigation'
import { ticketsNavigation } from '@/modules/tickets/navigation'
import { usersNavigation } from '@/modules/users/navigation'
import type { NavItem } from '@/shared/layout/navigation'

const leaves = [
  ...dashboardNavigation,
  ...usersNavigation,
  ...accessNavigation,
  ...segmentationNavigation,
  ...companiesNavigation,
  ...notificationsNavigation,
  ...newsNavigation,
  ...financeNavigation,
  ...ticketsNavigation,
  ...projectsNavigation,
  ...auditNavigation,
  ...exportsNavigation,
  ...settingsNavigation,
]

/** Item de módulo pela rota; falha cedo se um módulo mudar de rota sem atualizar o menu. */
function leaf(to: string, overrides: Partial<NavItem> = {}): NavItem {
  const item = leaves.find((candidate) => candidate.to === to)
  if (!item) throw new Error(`Item de menu inexistente: ${to}`)

  return { ...item, ...overrides }
}

/**
 * Menu lateral do painel, na ordem de exibição, organizado em seções (`group`)
 * e grupos recolhíveis (`children`). Ao criar um módulo, exporte
 * `<modulo>Navigation` em src/modules/<modulo>/navigation.ts, inclua em
 * `leaves` e posicione o item aqui.
 */
export const navigation: NavItem[] = [
  leaf('/'),

  // Usuários e acessos
  {
    to: '/users',
    labelKey: 'nav.users',
    icon: Users,
    group: 'nav.groups.users',
    children: [leaf('/users', { labelKey: 'nav.allUsers' }), leaf('/access-requests')],
  },
  {
    to: '/admins',
    labelKey: 'nav.team',
    icon: UserCog,
    group: 'nav.groups.users',
    children: [leaf('/admins'), leaf('/roles')],
  },

  // Cadastros
  leaf('/segmentation'),
  leaf('/app-modules'),
  leaf('/companies'),

  // Comunicação
  leaf('/notifications/push'),
  {
    to: '/notifications/campaigns',
    labelKey: 'nav.email',
    icon: Mail,
    group: 'nav.groups.communication',
    children: [leaf('/notifications/campaigns'), leaf('/notifications/templates')],
  },

  // Conteúdo
  leaf('/news'),

  // Financeiro
  {
    to: '/finance/entries',
    labelKey: 'nav.finance',
    icon: Landmark,
    group: 'nav.groups.finance',
    children: [leaf('/finance/entries'), leaf('/finance/settings')],
  },

  // Atendimento
  {
    to: '/tickets/dashboard',
    labelKey: 'nav.tickets',
    icon: LifeBuoy,
    group: 'nav.groups.support',
    children: [leaf('/tickets/dashboard'), leaf('/tickets')],
  },

  {
    to: '/projects',
    labelKey: 'nav.projects',
    icon: FolderKanban,
    group: 'nav.groups.support',
    children: [leaf('/projects'), leaf('/projects/sprints'), leaf('/projects/tasks')],
  },

  // Relatórios e auditoria
  leaf('/audit/activities'),
  leaf('/audit/access-logs'),
  leaf('/exports'),

  // Sistema
  {
    to: '/settings',
    labelKey: 'nav.settings',
    icon: Settings,
    group: 'nav.groups.system',
    children: [leaf('/settings', { labelKey: 'nav.generalSettings' }), leaf('/notifications/settings')],
  },
]
